import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Copy, ChevronDown, Send } from "lucide-react";
import { toast } from "sonner";
import { CA, TELEGRAM_URL, X_URL } from "@/config";
import { scrollToId } from "@/lib/scroll";
import { copyCa, shortCa } from "@/lib/ca";
import { Eyebrow } from "./Reveal";
import { XIcon } from "./OfficialLinks";
import { useLanguage } from "@/i18n/LanguageContext";

const lineAnim = (i) => ({
  initial: { y: "112%" },
  animate: { y: 0 },
  transition: { duration: 0.9, delay: 0.15 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
});

export const Hero = () => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const imgRot = useTransform(scrollYProgress, [0, 1], [-2, 3]);

  const handleCopy = () => {
    if (copyCa()) toast.success(t("hero.toastCopied"));
    else toast(t("hero.toastSoon"));
  };

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-screen items-center overflow-hidden pt-24 pb-16"
    >
      <div className="hero-glow pointer-events-none absolute -right-40 top-1/4 h-[720px] w-[720px] rounded-full" />
      <div className="pointer-events-none absolute left-0 top-0 h-72 w-72 rounded-full bg-[#9945FF]/10 blur-[120px]" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: 0 }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Eyebrow>{t("hero.eyebrow")}</Eyebrow>
          </motion.div>

          <h1 className="font-display mt-6 text-5xl font-extrabold leading-[1.02] tracking-tight sm:text-6xl lg:text-7xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block text-[#F5F3FF]" {...lineAnim(0)}>
                {t("hero.t1")}
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-2">
              <motion.span
                className="block text-flow"
                {...lineAnim(1)}
              >
                {t("hero.t2")}
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-gray-300 sm:text-lg"
          >
            <span className="font-display text-base font-bold uppercase tracking-wide text-[#F5F3FF] sm:text-lg">
              {t("hero.journey")}
            </span>
            <br />
            {t("hero.body")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-3"
          >
            <button
              data-testid="hero-buy-btn"
              onClick={() => scrollToId("official-links")}
              className="bg-solana-gradient inline-flex items-center gap-2 rounded-full px-8 py-4 text-base font-bold text-white shadow-[0_4px_24px_rgba(153,69,255,0.3)] transition-transform hover:scale-[1.03]"
            >
              {t("hero.buy")}
            </button>
            <a
              data-testid="hero-telegram-btn"
              href={TELEGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-purple-500/40 bg-[#0E0E16] px-8 py-4 text-base font-bold text-purple-100 transition-colors hover:border-[#14F195]/60 hover:text-[#14F195]"
            >
              <Send size={17} />
              {t("hero.telegram")}
            </a>
            <a
              data-testid="hero-x-btn"
              href={X_URL}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="SOLMOO on X"
              className="flex h-[52px] w-[52px] items-center justify-center rounded-full border border-purple-500/40 bg-[#0E0E16] text-purple-100 transition-colors hover:border-[#14F195]/60 hover:text-[#14F195]"
            >
              <XIcon size={18} />
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-7"
          >
            <div
              data-testid="hero-ca-box"
              className="inline-flex flex-wrap items-center gap-x-4 gap-y-3 rounded-2xl border border-purple-500/25 bg-[#0E0E16]/80 px-5 py-3.5 backdrop-blur"
            >
              <div className="flex flex-col">
                <span className="font-display text-xs font-bold uppercase tracking-[0.2em] text-purple-300">
                  {t("hero.caLabel")}
                </span>
                <span className="font-jb text-sm text-purple-100/90">
                  {CA ? shortCa() : t("hero.caValue")}
                </span>
              </div>
              <div className="hidden h-9 w-px bg-purple-500/20 sm:block" />
              <button
                data-testid="hero-copy-ca-btn"
                onClick={handleCopy}
                className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition-colors ${
                  CA
                    ? "border-purple-500/40 bg-[#14141E] text-[#14F195] hover:border-[#14F195]/60"
                    : "cursor-not-allowed border-purple-500/25 bg-[#14141E] text-purple-300"
                }`}
              >
                <Copy size={15} />
                {CA ? t("hero.copyCa") : t("hero.comingSoon")}
              </button>
            </div>
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            style={{ y: imgY, rotate: imgRot }}
            className="relative mx-auto max-w-md"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="animate-float"
            >
              <img
                src="/mascots/cow-astronaut.png"
                alt="SOLMOO astronaut cow walking on the moon"
                className="w-full rounded-[2rem] border border-purple-500/25 object-cover shadow-[0_0_60px_rgba(153,69,255,0.2)]"
              />
            </motion.div>
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.1, duration: 0.6 }}
              className="animate-float absolute -left-4 top-8 rounded-full border border-[#14F195]/40 bg-[#0E0E16]/90 px-4 py-2 font-jb text-xs font-semibold text-[#14F195] backdrop-blur"
              style={{ animationDelay: "0.8s" }}
            >
              0% TAX
            </motion.span>
            <motion.span
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 1.25, duration: 0.6 }}
              className="animate-float absolute -right-3 bottom-16 rounded-full border border-[#9945FF]/40 bg-[#0E0E16]/90 px-4 py-2 font-jb text-xs font-semibold text-purple-300 backdrop-blur"
              style={{ animationDelay: "1.6s" }}
            >
              100% LP BURNED
            </motion.span>
          </motion.div>
        </div>
      </div>

      <button
        data-testid="hero-scroll-hint"
        onClick={() => scrollToId("chart")}
        className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1 text-purple-300/70 transition-colors hover:text-[#14F195] sm:flex"
        aria-label="Scroll to chart"
      >
        <span className="font-jb text-[10px] uppercase tracking-[0.3em]">{t("hero.scroll")}</span>
        <ChevronDown size={18} className="animate-bounce" />
      </button>
    </section>
  );
};
